import React, { useRef, useState } from 'react';
import api from '../api/api';
import { RestaurantAttendance } from '../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../utils/apiError';

const inputTime = (value: string | null) => value?.slice(0, 16) ?? '';
const ukraineDate = (date: Date) => new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);
export const preserveCivilTime = (input: string, original?: string | null) =>
  original && input === inputTime(original) ? original : input.length === 16 ? `${input}:00` : input;

const RestaurantAttendanceEditor: React.FC<{
  employmentId: number;
  original: RestaurantAttendance | null;
  defaultDate?: string;
  disabled?: boolean;
  onSaved: () => void | Promise<void>;
  onDeleted?: () => void | Promise<void>;
  onNew?: () => void;
  onSavingChange?: (saving: boolean) => void;
}> = ({ employmentId, original, defaultDate, disabled, onSaved, onDeleted, onNew, onSavingChange }) => {
  const [entry, setEntry] = useState({
    checkInAt: inputTime(original?.checkInAt ?? null) || (defaultDate ? `${defaultDate}T09:00` : ''),
    checkOutAt: inputTime(original?.checkOutAt ?? null),
    hourCoefficient: original?.hourCoefficient ?? 1, comment: original?.comment ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const checkInInput = useRef<HTMLInputElement>(null);
  const checkOutInput = useRef<HTMLInputElement>(null);
  const deleteEntry = () => {
    if (!original || !onDeleted || pending.current || disabled) return;
    const legacyNotice = original.legacyAttendanceId !== null
      ? ' Запис старого табелю залишиться незмінним.'
      : '';
    if (!window.confirm(`Видалити інтервал №${original.id} з табелю ресторану?${legacyNotice} Цю дію не можна скасувати; погоджені й закриті місяці видалити не можна.`))
      return;
    pending.current = true;
    setBusy(true);
    onSavingChange?.(true);
    setError('');
    void api.delete(`/restaurant-personnel/${employmentId}/attendance/${original.id}`, {
      data: { version: original.version },
    }).then(() => onDeleted()).catch(failure =>
      setError(extractApiErrorMessage(failure, 'Не вдалося видалити інтервал. Оновіть табель перед повтором.'))
    ).finally(() => {
      pending.current = false;
      setBusy(false);
      onSavingChange?.(false);
    });
  };
  return <form aria-label="Редактор інтервалу" noValidate onSubmit={event => {
    event.preventDefault();
    if (pending.current || disabled) return;
    if (checkInInput.current?.validity.badInput || checkOutInput.current?.validity.badInput) {
      setError('Заповніть дату й час повністю або очистьте поле.');
      return;
    }
    if (!event.currentTarget.reportValidity()) return;
    if (!entry.checkOutAt && entry.checkInAt.slice(0, 10) < ukraineDate(new Date()) &&
        !window.confirm('Ви зберігаєте інтервал за минулу дату без часу виходу. Він залишиться відкритим («відкрито») і блокуватиме інші інтервали після приходу. Продовжити?'))
      return;
    pending.current = true;
    setBusy(true);
    onSavingChange?.(true);
    setError('');
    void api.put(`/restaurant-personnel/${employmentId}/attendance/${original?.id ?? 0}`, {
      ...entry, version: original?.version ?? 0,
      checkInAt: preserveCivilTime(entry.checkInAt, original?.checkInAt),
      checkOutAt: entry.checkOutAt ? preserveCivilTime(entry.checkOutAt, original?.checkOutAt) : null,
    }).then(() => onSaved()).catch(failure =>
      setError(extractApiErrorMessage(failure, 'Не вдалося зберегти інтервал. Оновіть табель перед повтором.'))
    ).finally(() => {
      pending.current = false;
      setBusy(false);
      onSavingChange?.(false);
    });
  }}>
    <h4>{original ? `Редагування інтервалу №${original.id}` : 'Новий інтервал'}</h4>
    {error && <p role="alert">{error}</p>}
    <fieldset disabled={disabled || busy}>
      <legend>Окремий інтервал · цивільний час України</legend>
      <div className="restaurant-personnel__form">
        <label>Прихід (час України)<input ref={checkInInput} type="datetime-local" required step={60} value={entry.checkInAt}
          onChange={event => setEntry({ ...entry, checkInAt: event.target.value })} /></label>
        <label>Вихід (порожньо — відкрито)<input ref={checkOutInput} type="datetime-local" step={60} value={entry.checkOutAt}
          onChange={event => setEntry({ ...entry, checkOutAt: event.target.value })} /></label>
        <label>Годинний коефіцієнт<input type="number" min={0} step="any" required value={entry.hourCoefficient}
          onChange={event => setEntry({ ...entry, hourCoefficient: Number(event.target.value) })} /></label>
        <label>Примітка інтервалу<input maxLength={2000} value={entry.comment}
          onChange={event => setEntry({ ...entry, comment: event.target.value })} /></label>
      </div>
      <div className="restaurant-personnel__actions">
        <button type="submit">Зберегти інтервал</button>
        {original && onDeleted && <button type="button" onClick={deleteEntry}>Видалити інтервал</button>}
        {onNew && <button type="button" onClick={onNew}>Новий інтервал</button>}
      </div>
    </fieldset>
  </form>;
};
export default RestaurantAttendanceEditor;
